import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useToast } from '@/primer/components';
import { useInitiateOtp } from '@/api';
import { inviteEmailSchema } from '@/schemas';
import {
  clearOtpChallenge,
  DEFAULT_API_ERROR_MESSAGE,
  getApiErrorMessage,
  getApiErrorMessages,
  getSafeRedirect,
  OTP_TOAST,
  storeOtpChallenge,
  type ApiErrorMappingOptions,
} from '@/utils';
import { AuthEmailForm } from '@/components/ui/AuthEmailForm';

const OTP_SEND_FAILED = "We couldn't send a verification code. Please try again.";

const SIGNIN_ERROR_OPTIONS: ApiErrorMappingOptions = {
  messageMap: {
    ERROR_INITIATING_OTP: OTP_SEND_FAILED,
    'Unable to send code': OTP_SEND_FAILED,
  },
  customHandler: (error) =>
    getApiErrorMessages(error).some((message) => message.toLowerCase().includes('email'))
      ? 'Enter a valid email address.'
      : undefined,
};

export function SignInEmail() {
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: '/_public/signin/email' });
  const safeRedirect = getSafeRedirect(redirect);
  const initiateOtp = useInitiateOtp();
  const { showToast } = useToast();

  const [email, setEmail] = useState('');
  const [submitError, setSubmitError] = useState('');
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const canContinue = inviteEmailSchema.safeParse({ email }).success;

  const handleNext = async () => {
    if (!canContinue || initiateOtp.isPending) return;
    setSubmitError('');
    clearOtpChallenge();

    let otpChallenge;
    try {
      otpChallenge = await initiateOtp.mutateAsync({ email });
    } catch (err) {
      if (!mountedRef.current) return;
      setSubmitError(getApiErrorMessage(err, SIGNIN_ERROR_OPTIONS));
      return;
    }

    if (!mountedRef.current) return;

    if (!storeOtpChallenge('signin', otpChallenge, { email })) {
      setSubmitError(DEFAULT_API_ERROR_MESSAGE);
      return;
    }

    showToast(OTP_TOAST.CODE_SENT);
    navigate({ to: '/signin/otp', ...(safeRedirect && { search: { redirect: safeRedirect } }) });
  };

  return (
    <AuthEmailForm
      email={email}
      onEmailChange={setEmail}
      onSubmit={() => void handleNext()}
      canContinue={canContinue}
      isSubmitting={initiateOtp.isPending}
      submitError={submitError}
      testIdPrefix="signin-email"
    />
  );
}

export default SignInEmail;
